// Hermes use case — RunTeamTurn: o pipeline do modo equipe (sprint subagents-team).
//
//   ensureAgent → recall (memória, best-effort) → PLAN (líder) → decide →
//   fan-out com briefing por operário (REUSO do driver D14) → SÍNTESE como o
//   merge injetado do fan-out → append da síntese na memória do líder.
//
// Implementa a capability que a sprint Hermes deferiu em D14-(a): fan-out
// disparado pelo RACIOCÍNIO do agente, não por clique do usuário. O contrato
// com o chat é o C2 do 03-PLAN: `{ mode:'solo' }` manda o caller seguir o
// fluxo normal; `{ mode:'team', ... }` carrega a resposta sintetizada.
//
// Ports injetados (Dependency Rule):
//   ensureAgent  — ({ root }) → lead agent record
//   planner      — planTeam (já fallback-first)
//   synthesizer  — synthesizeTeam (já fallback-first)
//   fanOut       — ({ agent, task, subtasks, merge, onChildEvent, signal,
//                    onStarted }) → outcome do fanOutAndMerge (composition root
//                    liga no driver real; é o MESMO driver, nunca um segundo)
//   recall       — (agentId, { query }) → { block } | null (budget-capped no Worker)
//   appendMemory — (agentId, mem) → void (best-effort)
//   clock        — () => ms epoch

import { TEAM_LIMITS, decideMode, buildBriefing } from '../domain/teamPlan.js';
import { deriveWorkerStatus } from '../domain/squadCall.js';
import { clearPlanTeamCache } from './planTeam.js';

const noRecall = async () => null;
const noAppend = async () => {};

// E2-WS3-T01 — normalize a provider usage object to a token count. Handles both
// OpenAI-compat (prompt/completion) and Anthropic (input/output) shapes. Pure;
// framework-free (the registry pricer is injected as a port — the Dependency
// Rule forbids the application layer importing src/lib).
function usageTokens(usage) {
  if (!usage || typeof usage !== 'object') return 0;
  const inT = Number(usage.input_tokens ?? usage.prompt_tokens ?? 0) || 0;
  const outT = Number(usage.output_tokens ?? usage.completion_tokens ?? 0) || 0;
  return inT + outT;
}

// Default leader-usage pricer: honor an explicit OpenRouter-reported cost; else
// $0 (the composition root injects the registry-backed pricer for real pricing).
const defaultPriceUsage = (usage) => {
  const reported = Number(usage?.cost ?? usage?.total_cost);
  return Number.isFinite(reported) && reported >= 0 ? reported : 0;
};

// G4-WS1-T02 — the team pipeline's pure/near-pure phases (recall, subtask briefing,
// child-status derivation, stats, the final result decision) were inline in one
// complexity-36 / 146-line function. Each is now a module-private helper; runTeamTurn is
// a thin orchestrator over the injected ports. Byte-identical: same events, same order,
// same returns, same best-effort swallows — runTeamTurn.test unchanged.

// E2-WS3-T01 — accumulate the LEADER's own LLM cost (PLAN + SYNTHESIS), which the stats
// previously ignored (only children were summed). The leader runs at the session brain
// model; price each usage via the injected pricer. Mutable accumulator so runTeamTurn
// stays a thin orchestrator (the fold + try/catch lives here, not in the god-function).
function makeLeaderUsageFolder(priceUsage, leaderModel) {
  let leaderTokens = 0;
  let leaderCostUsd = 0;
  return {
    fold(usage) {
      if (!usage || typeof usage !== 'object') return;
      leaderTokens += usageTokens(usage);
      try { leaderCostUsd += Number(priceUsage(usage, leaderModel)) || 0; } catch { /* never break the turn */ }
    },
    get leaderTokens() { return leaderTokens; },
    get leaderCostUsd() { return leaderCostUsd; },
  };
}

// Failed fan-out result: even on a failed fan-out the PLAN already ran (and was billed) —
// reflect its cost rather than reporting $0.
function teamFailedFanoutResult({ error, plan, parentRunId, usage, durationMs, synthSource }) {
  return {
    mode: 'team',
    failed: true,
    error,
    finalText: '',
    plan,
    parentRunId,
    children: [],
    stats: { tokens: usage.leaderTokens, costUsd: usage.leaderCostUsd, durationMs, synthSource, leaderTokens: usage.leaderTokens, leaderCostUsd: usage.leaderCostUsd },
  };
}

// 2. Recall the leader's memory (best-effort: sem memória ≠ sem equipe).
async function recallMemoryBlock(recall, leadId, text) {
  try {
    const mem = await recall(leadId, { query: text });
    if (typeof mem?.block?.content === 'string') return mem.block.content;
  } catch {
    /* sem memória ≠ sem equipe */
  }
  return '';
}

// 4. subtask + briefing determinístico (a "memória de equipe" de cada operário) + a
// doutrina do operário (E8-T02) quando injetada.
function buildTeamSubtasks(plan, text, workerDoctrine) {
  const doctrineBlock = workerDoctrine ? `\n\n${workerDoctrine}` : '';
  return plan.subtasks.map(
    (s, i) => `${s.task}\n\n${buildBriefing({ objective: text, subtasks: plan.subtasks, index: i })}${doctrineBlock}`,
  );
}

// E3 — status 4-valorado por operário, derivado de SINAIS VERIFICÁVEIS (state do ledger +
// stopReason + se a síntese citou [Ai] + tamanho). Nunca de auto-relato.
function buildTeamChildren({ rawChildren, results, plan, mergedText }) {
  return rawChildren.map((c, i) => {
    const r = results[i] || {};
    const finalText = typeof r.finalText === 'string' ? r.finalText : '';
    return {
      id: c.id,
      title: plan.subtasks[i]?.title || `A${i + 1}`,
      state: c.state,
      tokens: c.tokens ?? null,
      status: deriveWorkerStatus({
        runState: c.state,
        ok: r.ok,
        stopReason: r.stopReason,
        citationCovered: mergedText.includes(`[A${i + 1}]`),
        finalText,
        finalTextLen: finalText.length,
      }),
    };
  });
}

// E2-WS3-T01 — stats: the children tokens/cost PLUS the leader's plan+synthesis cost.
function buildTeamStats({ rawChildren, leaderTokens, leaderCostUsd, durationMs, synthSource }) {
  return {
    tokens: rawChildren.reduce((a, c) => a + (Number(c.tokens) || 0), 0) + leaderTokens,
    costUsd: rawChildren.reduce((a, c) => a + (Number(c.cost) || 0), 0) + leaderCostUsd,
    durationMs,
    synthSource,
    leaderTokens,
    leaderCostUsd,
  };
}

// Unpack a successful/terminal fan-out outcome into the `base` result shape (children +
// stats + parentRunId). Keeps the outcome-defaulting (|| / ??) out of the orchestrator.
function buildTeamBase({ outcome, plan, parentRunId, usage, durationMs, synthSource }) {
  const rawChildren = outcome.children || [];
  const children = buildTeamChildren({ rawChildren, results: outcome.results || [], plan, mergedText: outcome.merged || '' });
  const stats = buildTeamStats({ rawChildren, leaderTokens: usage.leaderTokens, leaderCostUsd: usage.leaderCostUsd, durationMs, synthSource });
  return { mode: 'team', plan, parentRunId: outcome.run?.id ?? parentRunId, children, stats };
}

// Final result decision: cancelled / failed(lost) → carry the merged text; success → the
// synthesis becomes the leader's team memory (kind 'run', best-effort, só em sucesso).
async function finalizeTeamResult({ outcome, base, appendMemory, leadId, text }) {
  if (outcome.cancelled) return { ...base, cancelled: true, finalText: outcome.merged || '' };
  if (outcome.claimed === false || outcome.failed) {
    return { ...base, failed: true, error: outcome.error || 'run perdido', finalText: outcome.merged || '' };
  }
  const finalText = outcome.merged || '';
  if (finalText.trim()) {
    try {
      await appendMemory(leadId, {
        kind: 'run',
        name: String(text).slice(0, 60),
        content: finalText.slice(0, 4000),
        sourceRunId: base.parentRunId,
      });
    } catch {
      /* memória é upgrade, nunca bloqueio */
    }
  }
  return { ...base, finalText };
}

export function makeRunTeamTurn({
  ensureAgent,
  planner,
  synthesizer,
  fanOut,
  recall = noRecall,
  appendMemory = noAppend,
  clock = Date.now,
  // E2-WS3-T01 — price a leader (plan/synthesis) usage object → USD. Injected so
  // the application layer stays free of src/lib (Dependency Rule). Default = the
  // OpenRouter-reported-cost-or-0 pricer above; the composition root wires the
  // registry-backed one (modelRegistry) for full pricing.
  priceUsage = defaultPriceUsage,
  // E8-T02 — doutrina do operário (núcleo da LEI), INJETADA pelo composition
  // root (Dependency Rule: a fonte única vive em shared/, fora deste boundary).
  // '' = comportamento anterior (caminho de equipe rodava "sem os livros").
  workerDoctrine = '',
} = {}) {
  /**
   * runTeamTurn({ text, root, brain, cap, workerModel, signal, onEvent })
   * Eventos emitidos (C2): team_phase · team_plan · team_started · team_child ·
   * team_token. Nunca lança por falha de LLM (fallbacks); lança só se o próprio
   * ledger de runs falhar de forma irreconciliável — e MESMO isso vira retorno
   * `failed` aqui (o chat mostra erro padrão).
   */
  return async function runTeamTurn({
    text,
    root,
    brain,
    cap = TEAM_LIMITS.capDefault,
    workerModel = null,
    signal,
    onEvent,
  } = {}) {
    clearPlanTeamCache();
    const emit = (evt) => { try { onEvent?.(evt); } catch { /* UI hook é best-effort */ } };
    const t0 = clock();
    // E2 — squad_call_log: cada chamada LLM do orquestrador vira um evento
    // `squad_call` (o composition root bufferiza até o run existir e dá append).
    const emitCall = (entry) => emit({ type: 'squad_call', entry });

    // E2-WS3-T01 — the LEADER's own LLM cost (PLAN + SYNTHESIS) accumulates in the folder
    // (the stats previously ignored it — only children were summed).
    const usage = makeLeaderUsageFolder(priceUsage, brain?.model || null);

    // 1. Líder (cwd alinhado ao root da sessão — startRun snapshota cwd→run.root).
    const lead = await ensureAgent({ root });

    // 2-3. Recall (best-effort) + plano.
    emit({ type: 'team_phase', phase: 'planejando' });
    const memoryBlock = await recallMemoryBlock(recall, lead.id, text);
    const plan = await planner({ text, brain, memoryBlock, cap, signal, onCall: emitCall, onUsage: usage.fold });
    if (signal?.aborted || decideMode(plan) !== 'team') {
      return { mode: 'solo', reasoning: plan?.reasoning || '' };
    }
    emit({ type: 'team_plan', plan });

    // 4. Fan-out: subtasks com briefing (+ doutrina do operário), workers com o brain da
    // sessão por default.
    emit({ type: 'team_phase', phase: 'executando' });
    const subtasks = buildTeamSubtasks(plan, text, workerDoctrine);
    const workerAgent = { ...lead, model: workerModel || brain?.model || lead.model };
    // E2/E4 — 1 entrada de squad_call por DISPATCH de operário (modelo + tamanho do
    // briefing). NÃO loga as chamadas internas do agentLoop (fora de escopo).
    subtasks.forEach((sub, i) =>
      emitCall({ stage: 'worker', model: workerAgent.model, user: sub, contextChars: sub.length, index: i }),
    );

    let synthSource = null;
    const merge = async (children, results) => {
      emit({ type: 'team_phase', phase: 'sintetizando' });
      const out = await synthesizer({
        objective: text,
        plan,
        brain,
        children,
        results,
        signal,
        onToken: (delta) => emit({ type: 'team_token', delta }),
        onCall: (info) => emitCall({ ...info, stage: 'synthesis' }),
      });
      synthSource = out.source;
      usage.fold(out.usage); // E2-WS3-T01 — synthesis cost into leader totals
      return out.text;
    };

    let parentRunId = null;
    let outcome;
    try {
      outcome = await fanOut({
        agent: workerAgent,
        task: text,
        subtasks,
        merge,
        onChildEvent: (child) => emit({ type: 'team_child', child }),
        signal,
        onStarted: (run) => {
          parentRunId = run?.id ?? null;
          emit({ type: 'team_started', runId: parentRunId });
        },
      });
    } catch (e) {
      return teamFailedFanoutResult({ error: e?.message || String(e), plan, parentRunId, usage, durationMs: clock() - t0, synthSource });
    }

    const base = buildTeamBase({ outcome, plan, parentRunId, usage, durationMs: clock() - t0, synthSource });
    return finalizeTeamResult({ outcome, base, appendMemory, leadId: lead.id, text });
  };
}
